/**
 * Prepaid credit-pack SKU catalog — the shared, typed source of truth for the
 * one-off "top up your wallet" packs (credit-wallet billing, Phase 1). Mirrors
 * the "typed registry const + pure resolver" shape of `report-types.ts`.
 *
 * Money here is integer micro-EUR (µEUR, 1 EUR = 1_000_000): packs are priced in
 * the customer's display currency (EUR), NOT the internal µUSD ledger. The
 * credit ⇄ µUSD peg lives in `credit_config` (see `DEFAULT_CREDIT_UNIT_MICRO`);
 * these packs only assign a EUR price + face credits.
 *
 * All numbers below are DIALS, not locked commercial terms — provisional values
 * consistent with the 100 credits = €1 display peg (so a €20 pack ≈ 2,000 credits
 * before any volume bonus). Constraints baked in: min pack price ≥ €20; validity
 * tiered 3/6/12 months by size; volume discounts 10/20/30% on the larger packs
 * (expressed as `discount_bps` and folded into bonus credits).
 */

/** Micro-EUR per EUR (1 EUR = 1_000_000 µEUR). */
export const MICRO_PER_EUR = 1_000_000;

export interface ICreditPack {
	/** Stable SKU slug (e.g. "small") — the discriminant a purchase keys on. */
	code: string;
	/** Face credits granted (base at 100 cr/€1, plus any folded-in volume bonus). */
	credits: number;
	/** Pack price in integer micro-EUR (µEUR). */
	price_eur_micro: number;
	/** Credit validity from purchase, in months — tiered by pack size. */
	validity_months: 3 | 6 | 12;
	/** Volume discount in basis points (1000 = 10%) folded into bonus credits. */
	discount_bps: number;
}

export const CREDIT_PACKS: readonly ICreditPack[] = [
	// €20 → 2,000 credits at the 100 cr/€1 base peg. Entry pack, no volume bonus.
	{ code: "small", credits: 2_000, price_eur_micro: 20 * MICRO_PER_EUR, validity_months: 3, discount_bps: 0 },
	// €50 → base 5,000 cr + 10% bonus = 5,500 cr.
	{ code: "team", credits: 5_500, price_eur_micro: 50 * MICRO_PER_EUR, validity_months: 6, discount_bps: 1_000 },
	// €100 → base 10,000 cr + 20% bonus = 12,000 cr.
	{ code: "growth", credits: 12_000, price_eur_micro: 100 * MICRO_PER_EUR, validity_months: 12, discount_bps: 2_000 },
	// €250 → base 25,000 cr + 30% bonus = 32,500 cr.
	{ code: "scale", credits: 32_500, price_eur_micro: 250 * MICRO_PER_EUR, validity_months: 12, discount_bps: 3_000 },
];

/** Pure lookup: resolve a pack by its SKU code (undefined when unknown). */
export function getCreditPack(code: string): ICreditPack | undefined {
	return CREDIT_PACKS.find((pack) => pack.code === code);
}
